import React, { useState } from "react";
import { useRoom } from "./RoomProvider";
import { useAttentionQueue } from "../../hooks/useAttentionQueue";

/**
 * PulsePanel — the answer-in-two-seconds HUD.
 *
 * A quiet ribbon that never leaves the top of the Floor. Seven columns —
 * one for each question a dispatcher must answer at a glance. Numbers first,
 * highest severity visible without hovering, every row is clickable to focus.
 *
 * Layout (single row, 7 pods):
 *   NEEDS · <n>   |   RISKS · <n>   |   NEXT   |   LATE DRIVERS · <n>
 *   FAILING TPs · <n>   |   AI (5m) · <n>   |   Δ 60s · <n>
 *
 * When any pod has content, its first row previews under the title. Click
 * any pod to expand full list into a dropdown drawer. Escape or click-away
 * collapses it. Everything derived from state we already load — no fetches.
 */
export default function PulsePanel() {
  const q = useAttentionQueue();
  const { focusOn, approveChain, resolveChainAlert, resolveTripRiskAlert, archiveTrip } = useRoom();
  const [open, setOpen] = useState(null); // key of open drawer

  const toggle = (k) => setOpen(prev => (prev === k ? null : k));
  const closeAll = () => setOpen(null);

  const onRowClick = (row) => {
    if (!row) return;
    if (row.kind === "chain_proposal" && row.chain_id) {
      const d = row.suggested_driver;
      if (d) approveChain && approveChain(row.chain_id, d.driver_id, d.tp_id);
    } else if (row.kind === "chain_alert" && row.alert_id) {
      const o = row.suggested_option;
      if (o) resolveChainAlert && resolveChainAlert(row.alert_id, "reassign", o.driver_id, o.tp_id);
    } else if (row.kind === "trip_risk_alert" && row.alert_id) {
      const o = row.suggested_option;
      if (o) resolveTripRiskAlert && resolveTripRiskAlert(row.alert_id, "reassign", o.driver_id, o.tp_id);
      else focusOn && focusOn("realTrip", row.trip_id);
    } else if (row.trip_id) focusOn && focusOn("realTrip", row.trip_id);
    else if (row.offer_id) focusOn && focusOn("offer", row.offer_id);
    closeAll();
  };

  const onArchiveRow = (e, row) => {
    e.stopPropagation();
    if (row.trip_id) archiveTrip && archiveTrip(row.trip_id);
  };

  return (
    <aside className="pulse" data-testid="pulse-panel" onMouseLeave={closeAll}>
      <Pod
        k="needs" title="assign" count={q.counts.needs}
        preview={q.needs[0]} tone={severityTone(q.needs[0]?.severity)}
        open={open === "needs"} onToggle={() => toggle("needs")}
        rows={q.needs} onRowClick={onRowClick}
        emptyText="nothing pending · you're clear"
      />
      <Pod
        k="risks" title="prevent" count={q.counts.risks}
        preview={q.risks[0]} tone={severityTone(q.risks[0]?.severity)}
        open={open === "risks"} onToggle={() => toggle("risks")}
        rows={q.risks} onRowClick={onRowClick} onArchiveRow={onArchiveRow}
        emptyText="no imminent risk"
      />
      <NextActionPod next={q.nextAction} onClick={() => onRowClick(q.nextAction)} />
      <Pod
        k="late" title="chase" count={q.counts.late}
        preview={q.lateDrivers[0]} tone={severityTone(q.lateDrivers[0]?.severity)}
        open={open === "late"} onToggle={() => toggle("late")}
        rows={q.lateDrivers} onRowClick={onRowClick}
        emptyText="all drivers on time"
      />
      <Pod
        k="tps" title="review" count={q.counts.failing_tps}
        preview={q.failingTps[0] ? { label: q.failingTps[0].name, sub: `${q.failingTps[0].bad} bad of ${q.failingTps[0].total} in 1h` } : null}
        tone={q.counts.failing_tps > 0 ? "high" : null}
        open={open === "tps"} onToggle={() => toggle("tps")}
        rows={q.failingTps.map(x => ({ label: x.name, sub: `${x.bad}/${x.total} last hour`, severity: "med" }))}
        onRowClick={() => closeAll()}
        emptyText="all TPs steady"
      />
      <Pod
        k="ai" title="nova did" count={q.aiActions.length}
        preview={q.aiActions[0] ? { label: shortenWhisper(q.aiActions[0].text) } : null}
        tone="info"
        open={open === "ai"} onToggle={() => toggle("ai")}
        rows={q.aiActions.map(w => ({ label: w.text, sub: relativeTime(w.at), severity: "low" }))}
        onRowClick={() => closeAll()}
        emptyText="nova has been quiet"
      />
      <Pod
        k="delta" title="just changed" count={q.minuteChanges.length}
        preview={q.minuteChanges[0] ? { label: shortenWhisper(q.minuteChanges[0].text) } : null}
        tone="info"
        open={open === "delta"} onToggle={() => toggle("delta")}
        rows={q.minuteChanges.map(w => ({ label: w.text, sub: relativeTime(w.at), severity: "low" }))}
        onRowClick={() => closeAll()}
        emptyText="no changes"
      />
    </aside>
  );
}

function Pod({ k, title, count, preview, tone, open, onToggle, rows, onRowClick, onArchiveRow, emptyText }) {
  const cls = `pulse-pod pulse-pod--${tone || "ok"} ${open ? "pulse-pod--open" : ""} ${count === 0 ? "pulse-pod--empty" : ""}`;
  return (
    <div className={cls} data-testid={`pulse-${k}`}>
      <button className="pulse-btn" onClick={onToggle} disabled={count === 0}
              data-testid={`pulse-${k}-toggle`}>
        <span className="pulse-title">{title}</span>
        <span className="pulse-count">{count}</span>
      </button>
      {preview && count > 0 && (
        <div className="pulse-preview" onClick={onToggle}>
          <div className="pulse-preview-label">{preview.label}</div>
          {preview.sub && <div className="pulse-preview-sub">{preview.sub}</div>}
        </div>
      )}
      {count === 0 && <div className="pulse-empty">{emptyText}</div>}
      {open && rows.length > 0 && (
        <div className="pulse-drawer" data-testid={`pulse-${k}-drawer`}>
          {rows.map((row, i) => (
            <div key={i} className={`pulse-row-wrap pulse-row--${row.severity || "low"}`}>
              <button className="pulse-row" onClick={() => onRowClick(row)}
                      data-testid={`pulse-${k}-row-${i}`}>
                <span className="pulse-row-dot" />
                <div className="pulse-row-body">
                  <div className="pulse-row-label">{row.label}</div>
                  {row.sub && <div className="pulse-row-sub">{row.sub}</div>}
                </div>
              </button>
              {onArchiveRow && row.trip_id && (
                <button className="pulse-row-archive" onClick={(e) => onArchiveRow(e, row)}
                        data-testid={`pulse-${k}-row-archive-${i}`}
                        title="Archive this trip and clear the alert for good">
                  archive
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NextActionPod({ next, onClick }) {
  if (!next) {
    return (
      <div className="pulse-pod pulse-pod--next pulse-pod--empty" data-testid="pulse-next">
        <button className="pulse-btn"><span className="pulse-title">next</span></button>
        <div className="pulse-empty">nothing needs you</div>
      </div>
    );
  }
  return (
    <div className={`pulse-pod pulse-pod--next pulse-pod--${severityTone(next.severity)}`} data-testid="pulse-next">
      <button className="pulse-btn" onClick={onClick} data-testid="pulse-next-cta">
        <span className="pulse-title">next</span>
        <span className="pulse-count">→</span>
      </button>
      <div className="pulse-next-body" onClick={onClick}>
        <div className="pulse-next-verb">{next.verb || "handle"}</div>
        <div className="pulse-next-target">{next.label}</div>
      </div>
    </div>
  );
}

function severityTone(sev) {
  if (sev === "critical") return "critical";
  if (sev === "high") return "high";
  if (sev === "med") return "med";
  return "ok";
}

function shortenWhisper(text) {
  if (!text) return "";
  return text.length > 60 ? text.slice(0, 57) + "…" : text;
}

function relativeTime(at) {
  if (!at) return "";
  const secs = Math.max(0, Math.round((Date.now() - new Date(at).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}
